/**
 * Gantt-kaavion puhdas layout-logiikka (AB-WP6.15): tehtävien jäsennys,
 * aikajänteen laskenta, palkkien px-positiointi ja aikajanan tick-merkit.
 * Ei DOMia — nämä testataan suoraan; runtime.ts:n wireGantt piirtää.
 *
 * Kaikki päivämatikka UTC-keskiyöllä (aikavyöhyke ei siirrä päiviä). Päivät
 * ISO-muodossa YYYY-MM-DD.
 */

const DAY_MS = 86_400_000;

export interface GanttTask {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  progress: number; // 0..100
  dependencies: string[];
  color?: string;
}

export type GanttScale = 'day' | 'week' | 'month';

export interface TaskLayout {
  x: number;
  width: number;
  progressWidth: number;
  startIdx: number;
  endIdx: number;
}

export interface AxisTick {
  label: string;
  x: number;
}

function parseISO(d: unknown): number | null {
  if (typeof d !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const t = Date.parse(`${d}T00:00:00Z`);
  return Number.isNaN(t) ? null : t;
}

function coerce(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const p = JSON.parse(value);
      if (Array.isArray(p)) return p;
    } catch {
      /* rikkinäinen JSON */
    }
  }
  return [];
}

/** Jäsennä tehtävät; vaatii string-id:n ja kelvolliset ISO-päivät. */
export function parseTasks(value: unknown): GanttTask[] {
  const tasks: GanttTask[] = [];
  for (const t of coerce(value)) {
    if (!t || typeof t !== 'object') continue;
    const o = t as Record<string, unknown>;
    if (typeof o.id !== 'string') continue;
    if (parseISO(o.startDate) == null || parseISO(o.endDate) == null) continue;
    const prog = Number(o.progress);
    tasks.push({
      id: o.id,
      name: typeof o.name === 'string' ? o.name : o.id,
      startDate: o.startDate as string,
      endDate: o.endDate as string,
      progress: Number.isFinite(prog) ? Math.max(0, Math.min(100, prog)) : 0,
      dependencies: Array.isArray(o.dependencies)
        ? o.dependencies.filter((x): x is string => typeof x === 'string') : [],
      color: typeof o.color === 'string' ? o.color : undefined,
    });
  }
  return tasks;
}

/** Aikajänne: origin = varhaisin start, totalDays = origin→myöhäisin end (inkl.). */
export function taskSpan(tasks: GanttTask[]): { originMs: number; totalDays: number } | null {
  if (tasks.length === 0) return null;
  let minS = Infinity;
  let maxE = -Infinity;
  for (const t of tasks) {
    const s = parseISO(t.startDate)!;
    const e = parseISO(t.endDate)!;
    if (s < minS) minS = s;
    if (e > maxE) maxE = e;
  }
  return { originMs: minS, totalDays: Math.round((maxE - minS) / DAY_MS) + 1 };
}

/** Pikseliä per päivä valitulla aikaskaalalla. */
export function pxPerDayFor(scale: GanttScale): number {
  if (scale === 'day') return 36;
  if (scale === 'month') return 6;
  return 14; // week
}

/** Yhden tehtävän palkin sijainti/leveys/edistymisleveys px:einä. */
export function layoutTask(task: GanttTask, originMs: number, pxPerDay: number): TaskLayout | null {
  const s = parseISO(task.startDate);
  const e = parseISO(task.endDate);
  if (s == null || e == null) return null;
  const startIdx = Math.round((s - originMs) / DAY_MS);
  const endIdx = Math.round((e - originMs) / DAY_MS);
  const days = Math.max(1, endIdx - startIdx + 1);
  const width = days * pxPerDay;
  return { x: startIdx * pxPerDay, width, progressWidth: width * (task.progress / 100), startIdx, endIdx };
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Aikajanan tick-merkit valitulla skaalalla; x = päiväoffset * pxPerDay. */
export function axisTicks(originMs: number, totalDays: number, scale: GanttScale, pxPerDay: number): AxisTick[] {
  const ticks: AxisTick[] = [];
  if (scale === 'month') {
    // Kuukauden alut origin→loppu; ensimmäinen tick aina originissa.
    const origin = new Date(originMs);
    let y = origin.getUTCFullYear();
    let m = origin.getUTCMonth();
    ticks.push({ label: `${y}-${pad(m + 1)}`, x: 0 });
    for (;;) {
      m += 1;
      if (m > 11) { m = 0; y += 1; }
      const ms = Date.UTC(y, m, 1);
      const dayOffset = Math.round((ms - originMs) / DAY_MS);
      if (dayOffset >= totalDays) break;
      ticks.push({ label: `${y}-${pad(m + 1)}`, x: dayOffset * pxPerDay });
    }
    return ticks;
  }
  const step = scale === 'week' ? 7 : 1;
  for (let i = 0; i < totalDays; i += step) {
    const d = new Date(originMs + i * DAY_MS);
    const label = scale === 'day' ? `${d.getUTCDate()}` : `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
    ticks.push({ label, x: i * pxPerDay });
  }
  return ticks;
}
